import React, { useEffect, Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import ChatMessage from 'modules/Chat/ChatMessage';
import Spinner from 'components/Spinner';
import DateFormat from 'components/DateFormat';
import ChatContainer from 'modules/Chat/ChatContainer';
import { CHAT_ROUTES } from 'utils/constants/chat';
import { DATE_FORMAT } from 'utils/constants/formats';
import { useConversation } from 'hooks/useConversation/useConversation';
import moment from 'moment-timezone';
import ChatSendMessage from 'modules/Chat/ChatSendMessage';
import { useTranslation } from 'react-i18next';

const ChatConversation = ({ className }) => {
  const { t } = useTranslation('Chat');
  const endOfListRef = React.createRef();
  const {
    handleGoToRoute,
    messages,
    conversation,
    loading,
    handleSendMessage,
    conversationId,
    sendingMessage,
    clearConversationId,
    messagesError,
    conversationDataError
  } = useConversation();

  // Keep scroll position to the last message of the list.
  useEffect(() => {
    if (!!endOfListRef.current && !!endOfListRef.current.scrollIntoView) {
      endOfListRef.current.scrollIntoView();
    }
  }, [endOfListRef, messages]);

  const handleBackButton = useCallback(() => {
    clearConversationId();
    handleGoToRoute(CHAT_ROUTES.CONVERSATIONS_LIST);
  }, [handleGoToRoute, clearConversationId]);

  const renderMessagesAndDates = useCallback(() => {
    if (!messages || messages.length === 0) return null;

    if (messagesError || conversationDataError) {
      return (
        <div
          className="chat-conversation__error"
          data-testid="chat-conversation__error"
        >
          {t('generalError')}
        </div>
      );
    }

    if (loading) {
      return (
        <div
          className="chat-conversation__loading"
          data-testid="chat-conversation__loading"
        >
          <Spinner />
        </div>
      );
    }

    return messages.map((message, i, arr) => {
      const isFirst = i === 0;
      const prevMessage = arr[i - 1] || {};
      const currentMessageDay = moment(message.created_at).startOf('day');
      const prevMessageDay = moment(prevMessage.created_at).startOf('day');
      const renderDate = isFirst || currentMessageDay.isAfter(prevMessageDay);
      return (
        <Fragment key={`${i}_${message?.created_at}`}>
          {renderDate && (
            <div
              className="chat-conversation__messages-date"
              data-testid="chat-conversation__messages-date"
            >
              <DateFormat date={moment(currentMessageDay).format()} />
            </div>
          )}
          <ChatMessage
            sender={message?.sender}
            message={message?.message}
            timestamp={message?.created_at}
            attachments={message?.attachments}
          />
        </Fragment>
      );
    });
  }, [conversationDataError, loading, messages, messagesError, t]);

  return (
    <div className={className} data-testid="chat-conversation">
      <div className="chat-conversation">
        <ChatContainer
          handleBack={handleBackButton}
          subtitle={
            conversation?.journey
              ? `${moment(conversation?.journey?.start_time_from)
                  .tz(conversation?.journey?.start_time_from_tz)
                  .format(DATE_FORMAT)} | ${conversation?.subject}`
              : ''
          }
          title={
            conversation?.journey
              ? `${conversation?.journey?.country_from || ''} ${
                  conversation?.journey?.label_from
                } > ${conversation?.journey?.country_to || ''} ${
                  conversation?.journey?.label_to
                }`
              : conversation?.subject
          }
          invertTitles
        >
          <div className="chat-conversation__container">
            {renderMessagesAndDates()}
            <div ref={endOfListRef} />
          </div>
          <ChatSendMessage
            handleSendMessage={handleSendMessage}
            loading={loading}
            isStarted={!!conversationId}
            isSending={sendingMessage}
          />
        </ChatContainer>
      </div>
    </div>
  );
};

ChatConversation.propTypes = {
  className: PropTypes.string
};

export default ChatConversation;
